
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'HR', 'SPAN', 'DIV',
  'STRONG', 'B', 'EM', 'I', 'U', 'S',
  'A', 'UL', 'OL', 'LI', 'BLOCKQUOTE',
  'H1', 'H2', 'H3',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH',
])

const DROP_WITH_CONTENT = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA',
  'LINK', 'META', 'BASE', 'SVG', 'MATH', 'NOSCRIPT', 'TEMPLATE', 'AUDIO', 'VIDEO', 'CANVAS',
])

const GLOBAL_ATTR = new Set(['style'])
const TAG_ATTR = {
  A: new Set(['href', 'target', 'rel']),
  TABLE: new Set(['width', 'align']),
  TD: new Set(['align', 'colspan', 'rowspan']),
  TH: new Set(['align', 'colspan', 'rowspan']),
}

const SAFE_URI = /^(?:https?:|mailto:|tel:)/i
const UNSAFE_CSS = /expression\s*\(|url\s*\(|javascript:|@import/i

const cleanAttributes = element => {
  const allowed = TAG_ATTR[element.tagName]

  for (const attribute of [...element.attributes]) {
    const name = attribute.name.toLowerCase()

    if (!GLOBAL_ATTR.has(name) && !allowed?.has(name)) {
      element.removeAttribute(attribute.name)
      continue
    }

    if (name === 'href' && !SAFE_URI.test(attribute.value.trim())) element.removeAttribute(attribute.name)
    if (name === 'style' && UNSAFE_CSS.test(attribute.value)) element.removeAttribute(attribute.name)
  }
}

const cleanChildren = node => {
  for (const child of [...node.childNodes]) {
    if (child.nodeType === Node.TEXT_NODE) continue

    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove()
      continue
    }

    if (DROP_WITH_CONTENT.has(child.tagName)) {
      child.remove()
      continue
    }

    cleanChildren(child)

    if (ALLOWED_TAGS.has(child.tagName)) cleanAttributes(child)
    else child.replaceWith(...child.childNodes)
  }
}

export const sanitizeHtml = html => {
  const input = String(html ?? '')

  if (!input.trim()) return ''

  const doc = new DOMParser().parseFromString(`<body>${input}</body>`, 'text/html')

  cleanChildren(doc.body)

  return doc.body.innerHTML
}

const INLINE_STYLES = {
  P: 'margin:0 0 12px',
  H1: 'margin:0 0 12px;font-size:24px;line-height:1.3',
  H2: 'margin:0 0 12px;font-size:20px;line-height:1.3',
  H3: 'margin:0 0 12px;font-size:17px;line-height:1.3',
  UL: 'margin:0 0 12px;padding-left:22px',
  OL: 'margin:0 0 12px;padding-left:22px',
  LI: 'margin:0 0 4px',
  BLOCKQUOTE: 'margin:0 0 12px;padding-left:12px;border-left:3px solid #e0e0e0;color:#555555',
  A: 'color:#1976d2;text-decoration:underline',
  HR: 'border:none;border-top:1px solid #e0e0e0;margin:16px 0',
  TABLE: 'border-collapse:collapse;margin:0 0 12px',
  TD: 'padding:6px 10px;border:1px solid #e0e0e0',
  TH: 'padding:6px 10px;border:1px solid #e0e0e0;text-align:left;background:#fafafa',
}

const isBlockLevel = node => ['P', 'H1', 'H2', 'H3', 'LI', 'BLOCKQUOTE'].includes(node.tagName)


export const toEmailHtml = html => {
  const clean = sanitizeHtml(html)

  if (!clean.trim()) return ''

  const doc = new DOMParser().parseFromString(`<body>${clean}</body>`, 'text/html')

  for (const node of doc.body.querySelectorAll('*')) {
    const base = INLINE_STYLES[node.tagName]

    if (base) {
      const own = node.getAttribute('style')
      node.setAttribute('style', own ? `${base};${own}` : base)
    }

    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }

    if (isBlockLevel(node) && !node.textContent.trim() && !node.querySelector('br,img,hr')) {
      node.appendChild(doc.createElement('br'))
    }
  }

  return doc.body.innerHTML
}

export const htmlToText = html =>
  String(html ?? '')
    .replace(/<\s*\/\s*(p|div|h[1-3]|blockquote|tr|ul|ol)\s*>/gi, '\n\n')
    .replace(/<\s*br\s*\/?\s*>|<\s*\/\s*li\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

const escapeHtml = text =>
  String(text ?? '').replace(/[&<>]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[character])

export const textToHtml = text => {
  const value = String(text ?? '').trim()

  if (!value) return ''

  return value
    .split(/\n{2,}/)
    .map(block => `<p>${escapeHtml(block).replace(/\n/g, '<br />')}</p>`)
    .join('')
}

// The editor's empty state is '<p></p>', so a tag-count test would call that content.
export const isBlank = html =>
  !String(html ?? '')
    .replace(/<(br|hr|img)\b[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()
